import { Link } from 'react-router-dom'
import {
  ArrowRight, Plug, Wrench, Phone, DollarSign, Zap, ShieldCheck,
} from 'lucide-react'
import Button from '../components/ui/Button'
import SEO from '../components/SEO'

const BREADCRUMB = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://myschoolportal.xyz' },
    { '@type': 'ListItem', position: 2, name: 'Hardware Solutions', item: 'https://myschoolportal.xyz/products' },
  ],
}

const PRODUCT_SOLUTIONS = [
  {
    id: 'payment-receipts',
    image: 'https://images.unsplash.com/photo-1634733988138-bf2c3a2a13fa?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A payment terminal printing a paper receipt',
    problem: 'Still using receipt books? Parents keep losing them.',
    pain: 'Handwritten receipts fade, get lost in bags, and create disputes. You spend hours reconciling payments that should be straightforward.',
    solution: 'Payment Receipt Solution',
    benefits: [
      'Print clear receipts instantly at payment',
      'Automatic duplicate for your records',
      'Works directly with the payment system',
      'No more "I paid but lost the receipt" disputes',
    ],
  },
  {
    id: 'attendance-tracking',
    image: 'https://images.unsplash.com/photo-1585079374502-415f8516dcc3?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A glowing fingerprint on a biometric scanner',
    problem: 'Staff sign for each other. Students answer for absent friends.',
    pain: 'Paper registers are easy to manipulate. Teachers mark attendance for colleagues who arrive late. Students call out "present" for their friends. You never know who is really there.',
    solution: 'Biometric Attendance System',
    benefits: [
      'Staff and students check in with their own fingerprint',
      'Impossible to clock in for someone else',
      'Attendance data syncs to dashboard instantly',
      'Parents get SMS when child arrives (optional)',
    ],
  },
  {
    id: 'document-printing',
    image: 'https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A high-volume office document printer',
    problem: 'Report card printing takes all weekend.',
    pain: 'Your office printer jams constantly, runs out of ink mid-job, and takes 30 seconds per page. Printing 500 report cards becomes a nightmare.',
    solution: 'High-Volume Document Printer',
    benefits: [
      'Print 500 report cards in under an hour',
      'Sharp text and graphics every time',
      'Toner lasts for thousands of pages',
      'Built for heavy school workloads',
    ],
  },
  {
    id: 'id-cards',
    image: 'https://images.unsplash.com/photo-1671376354112-6de3d08b97af?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A plastic ID card on a lanyard',
    problem: 'Paying someone else to print ID cards costs too much.',
    pain: 'Every time a student loses their ID, you pay again. New students wait weeks. You have no control over the design or turnaround time.',
    solution: 'ID Card Printing System',
    benefits: [
      'Print new cards on demand in minutes',
      'Add photos, student numbers, and barcodes',
      'Design your own card layout once',
      'Replace lost cards for pennies, not cedis',
    ],
  },
  {
    id: 'barcode-scanning',
    image: 'https://images.unsplash.com/photo-1758543102397-e14b5dfdd8bd?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A handheld barcode scanner in its charging dock',
    problem: 'Typing student numbers at lunch creates long queues.',
    pain: 'Staff manually enter 6-digit student numbers while hungry kids wait in line. Typos mean wrong accounts get charged. It is slow and frustrating for everyone.',
    solution: 'ID Card Scanner',
    benefits: [
      'Scan ID cards in under 1 second',
      'Works for feeding, library, and attendance',
      'Zero typing errors',
      'Move 200 students through lunch in minutes',
    ],
  },
  {
    id: 'backup-power',
    image: 'https://images.unsplash.com/photo-1577722421736-b5578e92414d?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A compact backup power unit',
    problem: 'Power cuts mean you lose work and cannot operate.',
    pain: 'When ECG cuts power, your computers shut down, unsaved work disappears, and you cannot process payments or print documents until power returns.',
    solution: 'Backup Power System',
    benefits: [
      'Computers stay on during power cuts',
      'Finish what you are working on safely',
      'Protects equipment from power surges',
      'Keep operating during short outages',
    ],
  },
  {
    id: 'network-setup',
    image: 'https://images.unsplash.com/photo-1606904825846-647eb07f5be2?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A wireless network router',
    problem: 'WiFi only works in the office, nowhere else.',
    pain: 'Teachers cannot take attendance on tablets. The accountant cannot process payments from the front desk. Everyone crowds into one room to use the system.',
    solution: 'Campus Network Infrastructure',
    benefits: [
      'Strong WiFi signal across your entire campus',
      'Staff work from classrooms, not just the office',
      'Secure network with separate guest access',
      'Professional installation and configuration',
    ],
  },
  {
    id: 'security-cameras',
    image: 'https://images.unsplash.com/photo-1549109926-58f039549485?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A white CCTV security camera mounted on a wall',
    problem: 'You need to know what happens when you are not there.',
    pain: 'Items go missing. Incidents happen between students. Parents ask questions you cannot answer because nobody saw what really happened.',
    solution: 'Security Camera System',
    benefits: [
      'Record key areas like gates, playgrounds, and corridors',
      'Review footage when incidents are reported',
      'Visible cameras discourage bad behavior',
      'Remote viewing from your phone',
    ],
  },
  {
    id: 'desktop-computers',
    image: 'https://images.unsplash.com/photo-1575318633968-0383e7d07ca0?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A desktop computer and monitor on an office desk',
    problem: 'Slow, old computers are holding your admin team back.',
    pain: 'Staff wait minutes for the system to load. Multiple tabs cause freezes. Old machines crash during busy periods - report card season, fee collection, end of term - right when you can least afford downtime.',
    solution: 'School Admin Desktop Computers',
    benefits: [
      'Fast processors built for multi-tab admin work',
      'Pre-configured and ready to use on arrival',
      'Compatible with MySchoolPortal out of the box',
      'Durable builds suited for school environments',
    ],
  },
  {
    id: 'laptops',
    image: 'https://images.unsplash.com/photo-1541807084-5c52b6b3adef?auto=format&fit=crop&w=700&q=80',
    imageAlt: 'A laptop open on a wooden desk',
    problem: 'Teachers and staff are tied to one room to do their work.',
    pain: 'When the only computer is in the front office, teachers cannot enter grades from the staffroom, and management cannot check reports during meetings. Everything flows through one bottleneck.',
    solution: 'Staff & Teacher Laptops',
    benefits: [
      'Work from anywhere on campus',
      'Teachers enter grades and attendance from the classroom',
      'Runs MySchoolPortal smoothly on battery',
      'Lightweight and easy to carry between buildings',
    ],
  },
]

const REASONS = [
  { icon: Plug, title: 'Everything works together', desc: 'The hardware integrates directly with your MySchoolPortal account. No compatibility headaches.' },
  { icon: Wrench, title: 'Compatible out of the box', desc: 'Every item we sell is tested to work with MySchoolPortal. No configuration headaches.' },
  { icon: Phone, title: 'One number for everything', desc: 'Software problem? Hardware issue? Call the same support team. We handle it all.' },
  { icon: DollarSign, title: 'School budgets in mind', desc: 'We know school finances. Flexible payment plans and honest pricing, no hidden costs.' },
  { icon: Zap, title: 'Fast turnaround', desc: 'Most installations happen within a week of ordering. We know you need it working now.' },
  { icon: ShieldCheck, title: 'Tested in real schools', desc: 'We only sell equipment that we have seen work reliably in Ghanaian school environments.' },
]

const STEPS = [
  { title: 'Choose your equipment', desc: 'Select the solution you need from the list above.' },
  { title: 'Pay securely', desc: 'Complete your order by card or mobile money in under 2 minutes.' },
  { title: 'We confirm your order', desc: 'Our team reaches out within 24 hours to arrange delivery.' },
  { title: 'Receive your equipment', desc: 'Delivered to your school, ready to plug in and use.' },
]

function ProductCell({ product }) {
  return (
    <div className="bg-surface flex flex-col">
      <div className="aspect-[16/10] overflow-hidden border-b border-theme">
        <img
          src={product.image}
          alt={product.imageAlt}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="text-sm font-bold text-ink leading-snug mb-1.5">{product.problem}</p>
        <p className="text-xs text-[var(--ink-soft)] leading-relaxed mb-4">{product.pain}</p>
        <p className="font-boxy text-[10.5px] uppercase tracking-wide text-accent mb-1">Our solution</p>
        <p className="text-sm font-bold text-ink mb-4 pb-4 border-b border-theme">{product.solution}</p>
        <div className="flex flex-col gap-1.5 mb-4 flex-1">
          {product.benefits.map((b) => (
            <div key={b} className="flex gap-1.5 text-xs text-[var(--ink-soft)] leading-snug">
              <span className="text-accent font-bold flex-shrink-0">&#10003;</span>{b}
            </div>
          ))}
        </div>
        <Link to="/quote-request" state={{ product: { id: product.id, solution: product.solution } }}>
          <Button variant="ghost" size="sm" className="w-full justify-center">Request quote</Button>
        </Link>
      </div>
    </div>
  )
}

export default function Products() {
  return (
    <div className="pb-16">
      <SEO
        title="School Hardware & Equipment Ghana"
        description="Receipt printers, biometric attendance, ID card printers, scanners, backup power and campus networking. Supplied and installed by Tvantra."
        path="/products"
        schema={[BREADCRUMB]}
      />

      {/* Hero */}
      <section className="px-4 pt-16 pb-14 text-center bg-grid-pattern">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-display font-extrabold text-ink leading-[1.1] mb-5">
            The equipment you need<br />to run your school.
          </h1>
          <p className="text-lg text-[var(--ink-soft)] leading-relaxed">
            Great software needs great hardware. We supply the computers, printers, scanners, and infrastructure that make your daily operations faster and more reliable.
          </p>
        </div>
      </section>

      {/* Product grid */}
      <section className="px-4 py-10 border-t border-theme">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--rule-soft)] border border-theme rounded overflow-hidden">
            {PRODUCT_SOLUTIONS.map((product) => (
              <ProductCell key={product.id} product={product} />
            ))}
            <div className="bg-surface p-6 flex flex-col items-center justify-center text-center sm:col-span-2">
              <p className="text-sm font-bold text-ink mb-1.5">Need something else?</p>
              <p className="text-xs text-[var(--ink-soft)] leading-relaxed mb-4">
                Tell us what your school needs and we'll recommend the right equipment.
              </p>
              <Link to="/contact">
                <Button variant="ghost" size="sm">Talk to us</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why buy from us */}
      <section className="px-4 py-20 border-t border-theme bg-grid-pattern">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink mb-3">
              Why buy from us?
            </h2>
            <p className="text-[var(--ink-soft)] max-w-2xl mx-auto">
              We built the software, so we know exactly what hardware works best with it. Plus, you get one team handling everything instead of juggling multiple vendors.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--rule-soft)] border border-theme rounded overflow-hidden">
            {REASONS.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-surface p-6">
                <Icon size={20} className="text-accent mb-3" />
                <h3 className="text-sm font-bold text-ink mb-1.5">{title}</h3>
                <p className="text-xs text-[var(--ink-soft)] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 py-20 border-t border-theme">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink text-center mb-12">How it works</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <div key={s.title} className="text-center">
                <div className="w-11 h-11 rounded-full border flex items-center justify-center font-boxy text-sm font-bold text-accent mx-auto mb-3" style={{ borderColor: 'var(--accent)' }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="text-sm font-bold text-ink mb-1.5">{s.title}</h3>
                <p className="text-xs text-[var(--ink-soft)] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-20">
        <div className="max-w-3xl mx-auto">
          <div className="rounded p-10 sm:p-14 text-center" style={{ background: 'var(--panel-dark)' }}>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-white mb-3">Not sure what you need?</h2>
            <p className="max-w-lg mx-auto mb-7" style={{ color: 'var(--panel-dark-soft)' }}>
              Tell us about your school and the problems you are facing. We will recommend the right equipment for your situation and budget.
            </p>
            <Link to="/contact">
              <Button variant="primary" size="lg" className="!bg-[var(--accent-strong)] hover:!bg-[var(--accent)]">
                Talk to us <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
